import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { colors } from '../../styles/colors/';

import Icon, {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Body } from '../Body/Body';

const StyledModal = styled.div`
  position: absolute; /* Stay in place */
  display: ${(props) => (props.showModal ? 'flex;' : 'none;')};
  justify-content: center;
  align-items: center;
  z-index: 2; /* Sit on top */
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  background-color: rgb(0, 0, 0); /* Fallback color */
  background-color: rgba(0, 0, 0, 0.4); /* Black w/ opacity */
`;

const StyledModalContent = styled.div`
  position: relative;
  background-color: #fefefe;
  margin: auto;
  padding: 0;
  border: 1px solid #888;
  width: ${(props) => (props.isMobile ? '340px;' : '400px;')}
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
`;

const StyledModalHeader = styled.div`
  ${(props) =>
    props.warningModal === true &&
    css`
      background-color: ${colors['yellow']['1']};
      color: ${colors['gray']['9']};
      font-size: 16px;
      height: 70px;
    `} ${(props) =>
    props.successModal === true &&
    css`
      background-color: ${colors['green']['1']};
      color: ${colors['gray']['9']};
      font-size: 16px;
      height: 70px;
    `} ${(props) =>
    props.infoModal === true &&
    css`
      background-color: ${colors['indigo']['1']};
      color: ${colors['gray']['9']};
      font-size: 16px;
      height: 70px;
    `} ${(props) =>
    props.errorModal === true &&
    css`
      background-color: ${colors['red']['1']};
      color: ${colors['gray']['9']};
      font-size: 16px;
      height: 70px;
    `};
`;

const StyledModalHeaderContent = styled.div`
  display: flex;
  align-items: center;
  padding: 24px 33px;
`;

const StyledHeaderContent = styled.div`
  margin-left: 17.5px;
`;

const StyledIcon = styled(Icon)`
  font-size: 21px;
  color: ${(props) => props.color};
`;

const StyleModalBodyContainer = styled.div`
  padding: 24px 16px;
  min-height: 100px;
  font-weight: 400;
  margin-left: 16px;
  margin-right: 16px;
`;

const StyledYesButton = styled.button`
  padding: 5px 16px;
  background: ${colors['indigo']['7']};
  border-radius: 5px;
  border: 1px solid ${colors['indigo']['7']};
  color: white;
  font-size: 14px;
  cursor: pointer;
  height: ${(props) => (props.isMobile ? '48px;' : '35px;')};
`;

const StyledNoButton = styled.button`
  ${(props) =>
    props.warningModal &&
    css`
      padding: 5px 16px;
      color: ${colors['indigo']['7']};
      background: ${colors['white']['1']};
      border-radius: 5px;
      border-color: ${colors['indigo']['7']};
      border: 1px solid;
      font-size: 14px;
      margin-left: 8px;
      cursor: pointer;
      height: ${(props) => (props.isMobile ? '48px;' : '35px;')};
    `}

  ${(props) =>
    !props.warningModal &&
    css`
      padding: 5px 16px;
      background: ${colors['indigo']['7']};
      border-radius: 5px;
      border: 1px solid;
      color: white;
      font-size: 14px;
      height: ${(props) => (props.isMobile ? '48px;' : '35px;')};
      cursor: pointer;
    `};
`;

const StyledButtonContainer = styled.div`
  margin-left: 32px;
  margin-bottom: 32px;
`;

/**
 * Primary UI component for user interaction
 */
const Modal = ({
  headerContent,
  bodyContent,
  successModal,
  warningModal,
  infoModal,
  errorModal,
  YesButtonContent,
  NoButtonContent,
  isMobile,
  YesButtonAction,
  NoButtonAction,
}) => {
  const [showModal, setShowModal] = useState(true);

  const closeModal = () => {
    setShowModal(false);
    NoButtonAction();
  };

  return (
    <StyledModal showModal={showModal}>
      <StyledModalContent isMobile={isMobile}>
        <StyledModalHeader
          warningModal={warningModal}
          successModal={successModal}
          infoModal={infoModal}
          errorModal={errorModal}
        >
          <StyledModalHeaderContent>
            {successModal && (
              <StyledIcon component={CheckCircleOutlined} color={colors['green']['6']} />
            )}
            {warningModal && (
              <StyledIcon component={ExclamationCircleOutlined} color={colors['yellow']['6']} />
            )}
            {infoModal && (
              <StyledIcon component={InfoCircleOutlined} color={colors['indigo']['6']} />
            )}
            {errorModal && (
              <StyledIcon component={CloseCircleOutlined} color={colors['red']['6']} />
            )}
            <StyledHeaderContent>{headerContent}</StyledHeaderContent>
          </StyledModalHeaderContent>
        </StyledModalHeader>

        <StyleModalBodyContainer>
          <Body type="regular" content={bodyContent}></Body>
        </StyleModalBodyContainer>

        <StyledButtonContainer>
          {warningModal && (
            <StyledYesButton onClick={YesButtonAction} isMobile={isMobile}>
              {YesButtonContent}
            </StyledYesButton>
          )}
          <StyledNoButton onClick={closeModal} isMobile={isMobile} warningModal={warningModal}>
            {NoButtonContent}
          </StyledNoButton>
        </StyledButtonContainer>
      </StyledModalContent>
    </StyledModal>
  );
};

Modal.propTypes = {
  /**
   * Modal contents
   */
  headerContent: PropTypes.string,
  bodyContent: PropTypes.string,
  isMobile: PropTypes.bool,
  YesButtonContent: PropTypes.string,
  NoButtonContent: PropTypes.string,
};

Modal.defaultProps = {};

export { Modal };
